<template>
  <NavBar pageName="Translations"></NavBar>
  <v-container style="margin-top: 185px;">
    <v-btn variant="text" size="small" @click="$router.push('/settings')" class="mb-2">
      <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
      Settings
    </v-btn>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ t.translations ? t.translations.title : 'Translations' }}</span>
        <v-btn color="success" size="small" @click="openAddDialog">
          <v-icon class="mr-1">mdi-plus</v-icon>
          {{ t.common.add }}
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Filters Row -->
        <v-row class="mb-3" dense>
          <v-col cols="12" sm="3">
            <v-select
              v-model="selectedLocale"
              :items="localeOptions"
              item-title="text"
              item-value="value"
              label="Locale"
              variant="outlined"
              density="compact"
              @update:model-value="loadTranslations"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="3">
            <v-select
              v-model="selectedSection"
              :items="sectionOptions"
              item-title="text"
              item-value="value"
              label="Section"
              variant="outlined"
              density="compact"
              clearable
              @update:model-value="loadTranslations"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Search key or value"
              variant="outlined"
              density="compact"
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="2">
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              item-title="text"
              item-value="value"
              label="Status"
              variant="outlined"
              density="compact"
            ></v-select>
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="displayItems"
          :loading="loading"
          :items-per-page="20"
          class="elevation-1"
        >
          <template v-slot:[`item.section`]="{ item }">
            <v-chip size="small" color="blue-grey" variant="outlined">{{ item.section }}</v-chip>
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <v-chip size="small" :color="item.dbId ? 'success' : 'grey'" variant="flat">
              {{ item.dbId ? (t.translations ? t.translations.customized : 'Customized') : (t.translations ? t.translations.default_val : 'Default') }}
            </v-chip>
          </template>

          <template v-slot:[`item.defaultValue`]="{ item }">
            <span class="text-caption text-grey">{{ truncateText(item.defaultValue, 60) }}</span>
          </template>

          <template v-slot:[`item.currentValue`]="{ item }">
            <span class="font-weight-medium">{{ truncateText(item.currentValue, 60) }}</span>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn icon size="small" variant="text" color="primary" @click="openEditDialog(item)" title="Edit">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn v-if="item.dbId" icon size="small" variant="text" color="warning" @click="openResetDialog(item)" title="Reset to default">
              <v-icon>mdi-restore</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="showDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          {{ isEditing ? (t.translations ? t.translations.edit_translation : 'Edit Translation') : (t.translations ? t.translations.add_translation : 'Add Translation') }}
        </v-card-title>

        <v-card-text>
          <v-text-field
            v-model="formData.key"
            label="Key (dot notation)"
            variant="outlined"
            density="compact"
            :rules="[v => !!v || 'Key is required']"
            :disabled="isEditing"
            class="mb-3"
            placeholder="e.g. common.search"
          ></v-text-field>

          <v-select
            v-model="formData.locale"
            :items="localeOptions"
            item-title="text"
            item-value="value"
            label="Locale"
            variant="outlined"
            density="compact"
            class="mb-3"
          ></v-select>

          <!-- Show default value for reference when editing -->
          <v-alert v-if="isEditing && editingDefaultValue" type="info" variant="tonal" density="compact" class="mb-3">
            <strong>Default:</strong> {{ editingDefaultValue }}
          </v-alert>

          <v-textarea
            v-model="formData.value"
            label="Value"
            variant="outlined"
            density="compact"
            :rules="[v => v !== '' && v !== null && v !== undefined || 'Value is required']"
            rows="3"
          ></v-textarea>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">{{ t.common.cancel }}</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="saveTranslationHandler"
            :loading="saving"
            :disabled="!formData.key || !formData.locale || formData.value === '' || formData.value === null || formData.value === undefined"
          >
            {{ isEditing ? t.common.save : t.common.save }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reset (Delete) Confirmation Dialog -->
    <v-dialog v-model="showResetDialog" max-width="400px">
      <v-card>
        <v-card-title>{{ t.translations ? t.translations.reset_to_default : 'Reset to Default' }}</v-card-title>
        <v-card-text>
          {{ t.translations ? t.translations.confirm_reset : 'Are you sure you want to reset' }} <strong>{{ resettingItem?.key }}</strong> {{ t.translations ? t.translations.confirm_reset_suffix : 'to its default value?' }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showResetDialog = false">{{ t.common.cancel }}</v-btn>
          <v-btn color="warning" variant="flat" @click="confirmReset" :loading="deleting">
            {{ t.translations ? t.translations.reset_btn : 'Reset' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import store from "@/store";
import { log } from "@/composables/useLog";
import { t, messages } from "@/composables/useLanguages";
import { getTranslations, saveTranslation, deleteTranslation } from "@/composables/useTranslationsAPI";

export default defineComponent({
  name: 'TranslationsView',
  components: {
    NavBar
  },
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      dbTranslations: [],
      search: '',
      selectedLocale: 'ro',
      selectedSection: null,
      statusFilter: 'all',
      showDialog: false,
      showResetDialog: false,
      isEditing: false,
      editingDefaultValue: '',
      resettingItem: null,
      formData: {
        key: '',
        locale: 'ro',
        value: ''
      },
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      localeOptions: [
        { text: 'Romanian (ro)', value: 'ro' },
        { text: 'English (en)', value: 'en' },
        { text: 'Spanish (sp)', value: 'sp' },
        { text: 'French (fr)', value: 'fr' },
        { text: 'German (ge)', value: 'ge' },
        { text: 'Bulgarian (bg)', value: 'bg' },
        { text: 'Russian (ru)', value: 'ru' },
        { text: 'Italian (it)', value: 'it' },
        { text: 'Turkish (tr)', value: 'tr' },
        { text: 'Hungarian (hu)', value: 'hu' },
        { text: 'Chinese (cn)', value: 'cn' }
      ],
      statusOptions: [
        { text: 'All', value: 'all' },
        { text: 'Customized', value: 'customized' },
        { text: 'Default', value: 'default' }
      ]
    }
  },
  computed: {
    t() {
      return t.value;
    },
    token() {
      return store.getters.getToken;
    },
    headers() {
      return [
        { title: 'Key', key: 'key' },
        { title: 'Section', key: 'section', width: '120px' },
        { title: 'Default Value', key: 'defaultValue' },
        { title: 'Current Value', key: 'currentValue' },
        { title: 'Status', key: 'status', width: '120px' },
        { title: 'Actions', key: 'actions', sortable: false, width: '120px' }
      ];
    },
    sectionOptions() {
      // Extract sections from the JSON locale file
      const langKey = this.selectedLocale === 'cn' ? 'ci' : this.selectedLocale;
      const locale = messages[langKey] || messages.ro;
      const sections = Object.keys(locale).filter(k => typeof locale[k] === 'object');
      return [
        { text: 'All Sections', value: null },
        ...sections.map(s => ({ text: s, value: s }))
      ];
    },
    // Build the merged display list: all JSON keys + any DB-only keys
    displayItems() {
      const langKey = this.selectedLocale === 'cn' ? 'ci' : this.selectedLocale;
      const locale = messages[langKey] || messages.ro;
      const dbMap = {};

      // Index DB translations by key
      for (const dt of this.dbTranslations) {
        dbMap[dt.key] = dt;
      }

      const items = [];

      // Flatten JSON keys
      const flatten = (obj, prefix) => {
        for (const k of Object.keys(obj)) {
          if (k === 'country_code') continue;
          const fullKey = prefix ? `${prefix}.${k}` : k;
          if (typeof obj[k] === 'object' && obj[k] !== null) {
            flatten(obj[k], fullKey);
          } else {
            const section = fullKey.split('.')[0];
            const db = dbMap[fullKey];
            items.push({
              key: fullKey,
              section: section,
              defaultValue: String(obj[k]),
              currentValue: db ? db.value : String(obj[k]),
              dbId: db ? db.id : null
            });
            // Remove from dbMap so we can detect DB-only keys later
            if (db) delete dbMap[fullKey];
          }
        }
      };

      // Filter by section if selected
      if (this.selectedSection) {
        const sectionObj = locale[this.selectedSection];
        if (sectionObj && typeof sectionObj === 'object') {
          flatten(sectionObj, this.selectedSection);
        }
      } else {
        flatten(locale, '');
      }

      // Add DB-only keys (custom keys not in JSON)
      for (const key of Object.keys(dbMap)) {
        const db = dbMap[key];
        const section = key.split('.')[0];
        if (this.selectedSection && section !== this.selectedSection) continue;
        items.push({
          key: key,
          section: section,
          defaultValue: '-',
          currentValue: db.value,
          dbId: db.id
        });
      }

      // Apply search filter
      let filtered = items;
      if (this.search) {
        const s = this.search.toLowerCase();
        filtered = filtered.filter(item =>
          item.key.toLowerCase().includes(s) ||
          item.defaultValue.toLowerCase().includes(s) ||
          item.currentValue.toLowerCase().includes(s)
        );
      }

      // Apply status filter
      if (this.statusFilter === 'customized') {
        filtered = filtered.filter(item => item.dbId);
      } else if (this.statusFilter === 'default') {
        filtered = filtered.filter(item => !item.dbId);
      }

      return filtered;
    }
  },
  methods: {
    async loadTranslations() {
      this.loading = true;
      try {
        const response = await getTranslations(this.token, this.selectedLocale, null, null);
        if (!response.error) {
          this.dbTranslations = response.translations || [];
        } else {
          this.showMessage(response.message || 'Error loading translations', 'error');
        }
      } catch (err) {
        log("Error loading translations:", err);
        this.showMessage('Error loading translations', 'error');
      } finally {
        this.loading = false;
      }
    },

    openAddDialog() {
      this.isEditing = false;
      this.editingDefaultValue = '';
      this.formData = { key: '', locale: this.selectedLocale, value: '' };
      this.showDialog = true;
    },

    openEditDialog(item) {
      this.isEditing = true;
      this.editingDefaultValue = item.defaultValue;
      this.formData = {
        key: item.key,
        locale: this.selectedLocale,
        value: item.currentValue
      };
      this.showDialog = true;
    },

    closeDialog() {
      this.showDialog = false;
    },

    async saveTranslationHandler() {
      this.saving = true;
      try {
        const response = await saveTranslation(this.token, this.formData);
        if (!response.error) {
          this.showMessage(response.message || 'Translation saved', 'success');
          this.closeDialog();
          await this.loadTranslations();
        } else {
          this.showMessage(response.message || 'Error saving translation', 'error');
        }
      } catch (err) {
        log("Error saving translation:", err);
        this.showMessage('Error saving translation', 'error');
      } finally {
        this.saving = false;
      }
    },

    openResetDialog(item) {
      this.resettingItem = item;
      this.showResetDialog = true;
    },

    async confirmReset() {
      this.deleting = true;
      try {
        const response = await deleteTranslation(this.token, this.resettingItem.dbId);
        if (!response.error) {
          this.showMessage(response.message || 'Translation reset to default', 'success');
          this.showResetDialog = false;
          await this.loadTranslations();
        } else {
          this.showMessage(response.message || 'Error resetting translation', 'error');
        }
      } catch (err) {
        log("Error resetting translation:", err);
        this.showMessage('Error resetting translation', 'error');
      } finally {
        this.deleting = false;
      }
    },

    truncateText(text, maxLen) {
      if (!text) return '-';
      return text.length > maxLen ? text.substring(0, maxLen) + '...' : text;
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('ro-RO') + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    showMessage(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.showSnackbar = true;
    }
  },
  mounted() {
    this.loadTranslations();
  }
});
</script>
